import type { Contract, ContractTemplate } from '@shared/types';
import { PencilIcon } from '@heroicons/react/24/outline';

interface PreviewStepProps {
  data: Partial<Contract>;
  template?: ContractTemplate;
  onEditStep?: (stepName: string) => void;
}

export function PreviewStep({ data, template, onEditStep }: PreviewStepProps) {
  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-gray-900">Contract Preview</h2>

      <div className="space-y-6">
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">Contract Details</h3>
            {onEditStep && (
              <button
                onClick={() => onEditStep('Details')}
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
              >
                <PencilIcon className="w-4 h-4" />
                Edit
              </button>
            )}
          </div>
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            {data.title && <p><strong>Project:</strong> {data.title}</p>}
            <p><strong>Client:</strong> {data.clientName}</p>
            {data.estimatedStartDate && (
              <p><strong>Estimated Start Date:</strong> {new Date(data.estimatedStartDate).toLocaleDateString()}</p>
            )}
            {data.projectDuration && <p><strong>Project Duration:</strong> {data.projectDuration}</p>}
            <p><strong>Expiration Date:</strong> {data.expirationDate}</p>
          </div>
        </section>

        {template?.hasScope && data.executiveSummary && (
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">Executive Summary</h3>
              {onEditStep && (
                <button
                  onClick={() => onEditStep('Details')}
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  <PencilIcon className="w-4 h-4" />
                  Edit
                </button>
              )}
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="whitespace-pre-wrap">{data.executiveSummary}</p>
            </div>
          </section>
        )}

        {data.deliverables && data.deliverables.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                Deliverables ({data.deliverables.length})
              </h3>
              {onEditStep && (
                <button
                  onClick={() => onEditStep('Scope & Pricing')}
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  <PencilIcon className="w-4 h-4" />
                  Edit
                </button>
              )}
            </div>
            <div className="space-y-2">
              {data.deliverables.map((deliverable) => (
                <div key={deliverable.id} className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium">{deliverable.title}</p>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{deliverable.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {data.payments && data.payments.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                Payment Schedule ({data.payments.length})
              </h3>
              {onEditStep && (
                <button
                  onClick={() => onEditStep('Scope & Pricing')}
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  <PencilIcon className="w-4 h-4" />
                  Edit
                </button>
              )}
            </div>
            <div className="space-y-2">
              {data.payments.map((payment) => (
                <div key={payment.id} className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium text-lg">
                    ${payment.amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                    {payment.isRecurring && payment.recurringInterval && (
                      <span className="ml-2 text-sm text-gray-500 font-normal">
                        ({payment.recurringInterval})
                      </span>
                    )}
                  </p>
                  {payment.milestone && (
                    <p className="text-sm font-semibold text-gray-700 mt-1">{payment.milestone}</p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">{payment.description}</p>
                </div>
              ))}
              <div className="bg-primary-50 p-4 rounded-lg border-2 border-primary-200">
                <p className="font-bold text-lg text-gray-900">
                  Total: ${data.payments.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

